import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import api, { apiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatIDR } from '../lib/format';
import { MoneyInput } from './MoneyInput';
import { Stepper } from './transaction/BarangRow';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

/* Dialog Terima Pengembalian — SATU berkas dipakai halaman Transaksi maupun
   Pemantauan.

   Sebelum 21 Okt 2026 dialog ini ditulis dua kali di dua halaman, dan sudah mulai
   berbeda: yang di halaman Transaksi cuma punya 2 tombol, yang di Pemantauan punya
   3. Begitu denda ditambahkan, satu salinan pasti tertinggal. Jadi disatukan dulu
   sebelum fitur barunya ditempel.

   Aturan yang dijaga di sini:
     - Rusak  -> barang berhenti bisa dibooking, tapi tetap terhitung milik.
     - Hilang -> stok berkurang permanen.
     - Jumlah rusak + hilang tidak boleh melebihi yang disewa. Diperiksa di sini
       DAN di server; yang di sini supaya ketahuan sebelum ditekan, yang di server
       supaya tetap aman kalau tampilan dilewati.
     - Kolom denda hanya untuk owner. Karyawan tetap boleh menandai barang.
     - "Belum kembali" (31 Okt 2026): sebagian barang masih di customer. Order tetap
       aktif ("Belum lengkap") dan stok sisa tetap ditahan; bukan hilang, tidak
       mengurangi koleksi. Dialog yang sama dipakai lagi untuk melengkapi: yang
       ditampilkan hanya sisa.
*/

const SUMBER = [
  { v: 'cash', t: 'Tunai' },
  { v: 'bank_ops', t: 'Rek. Operasional' },
  { v: 'bank_utama', t: 'Rek. Utama' },
  { v: 'belum', t: 'Belum dibayar' },
];

const JENIS = ['Keterlambatan', 'Kerusakan', 'Kehilangan'];

function selisihHari(dari, sampai) {
  if (!dari || !sampai) return 0;
  const a = Date.parse(`${dari}T00:00:00`);
  const b = Date.parse(`${sampai}T00:00:00`);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.round((b - a) / 86400000);
}

function hariIni() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function ReturnDialog({ tx, open, onClose, onDone, testid = 'return' }) {
  const { user } = useAuth();
  const isOwner = user?.role !== 'karyawan';

  const [kondisi, setKondisi] = useState({ lengkap: true, belum: false, rusak: false, hilang: false, denda: false });
  const [jml, setJml] = useState({});
  const [catatan, setCatatan] = useState('');
  const [jenis, setJenis] = useState([]);
  const [nominal, setNominal] = useState('');
  const [sumber, setSumber] = useState('belum');
  const [kirim, setKirim] = useState(false);
  // Aksesoris bawaan set yang hilang: { Kalung: 2 }. Hanya catatan, tidak menyentuh stok.
  const [aksHilang, setAksHilang] = useState({});
  const [dendaKet, setDendaKet] = useState('');
  const [ketManual, setKetManual] = useState(false);

  // Setiap dialog dibuka ulang, isiannya dikosongkan. Tanpa ini, denda order
  // sebelumnya bisa ikut terbawa ke order berikutnya.
  useEffect(() => {
    if (!open) return;
    setKondisi({ lengkap: true, belum: false, rusak: false, hilang: false, denda: false });
    setJml({});
    setCatatan('');
    setJenis([]);
    setNominal('');
    setSumber('belum');
    setKirim(false);
    setAksHilang({});
    setDendaKet('');
    setKetManual(false);
  }, [open, tx?.id]);

  // Satu baris per (barang, ukuran) — termasuk bawahannya, karena bawahan punya
  // kode dan stok sendiri. Ukuran wajib ikut: stok dicatat per ukuran.
  const potongan = useMemo(() => {
    const peta = new Map();
    const tambah = (pid, kode, nama, size, qty) => {
      const q = Number(qty) || 0;
      if (!pid || q <= 0) return;
      const key = `${pid}|${size}`;
      const ada = peta.get(key);
      if (ada) ada.qty += q;
      else peta.set(key, { key, product_id: pid, code: kode || '', name: nama || '', size, qty: q });
    };
    if ((tx?.sisa_kembali || []).length) {
      // Melengkapi pengembalian: hanya barang yang masih di tangan customer.
      tx.sisa_kembali.forEach((x) => tambah(x.product_id, x.product_code, x.product_name, x.size, x.qty));
      return [...peta.values()];
    }
    (tx?.rows || []).forEach((r) => {
      Object.entries(r.sizes || {}).forEach(([s, q]) =>
        tambah(r.product_id, r.product_code, r.product_name, s, q));
      Object.entries(r.bawahan_sizes || {}).forEach(([s, q]) =>
        tambah(r.bawahan_product_id, r.bawahan_code, r.bawahan_name, s, q));
    });
    return [...peta.values()];
  }, [tx]);
  const lengkapi = (tx?.sisa_kembali || []).length > 0;

  // Aksesoris bawaan dijumlahkan per nama untuk SEMUA baris order (dua baris Baju
  // Bodo = dua set kalung). Order lama tidak punya aksesoris: bagian ini tidak muncul.
  const aksDibawa = useMemo(() => {
    const m = new Map();
    (tx?.rows || []).forEach((r) =>
      (r.aksesoris || []).forEach((a) => m.set(a.nama, (m.get(a.nama) || 0) + (Number(a.jumlah) || 0))));
    // Yang sudah dicatat hilang di tahap sebelumnya tidak dihitung dua kali.
    (tx?.pengembalian_sebagian || []).forEach((t) =>
      (t.aksesoris_hilang || []).forEach((a) => m.set(a.nama, Math.max(0, (m.get(a.nama) || 0) - (Number(a.pcs) || 0)))));
    return [...m.entries()].filter(([, dibawa]) => dibawa > 0).map(([nama, dibawa]) => ({ nama, dibawa }));
  }, [tx]);
  const aksKet = (peta) =>
    aksDibawa.filter((a) => (peta[a.nama] || 0) > 0).map((a) => `${a.nama} ${peta[a.nama]} pcs hilang`).join(', ');
  const aksJumlahHilang = aksDibawa.reduce((s, a) => s + (aksHilang[a.nama] || 0), 0);
  const ubahAks = (nama, dibawa, v) => {
    const peta = { ...aksHilang, [nama]: Math.max(0, Math.min(dibawa, v)) };
    setAksHilang(peta);
    // Ada yang hilang -> bagian denda terbuka sendiri dengan jenis Kehilangan dan
    // keterangannya terisi. Nominalnya tetap owner yang menentukan (hasil tawar).
    if (isOwner && Object.values(peta).some((n) => n > 0)) {
      setKondisi((s) => ({ ...s, denda: true }));
      setJenis((s) => (s.includes('Kehilangan') ? s : [...s, 'Kehilangan']));
    }
    if (!ketManual) setDendaKet(aksKet(peta));
  };

  const telat = Math.max(0, selisihHari(tx?.return_date, hariIni()));
  const angka = (v) => Number(String(v ?? '').replace(/[^0-9]/g, '') || 0);
  const ambil = (key, jns) => angka(jml[`${key}|${jns}`]);
  const setAmbil = (key, jns, v) =>
    setJml((s) => ({ ...s, [`${key}|${jns}`]: String(v).replace(/[^0-9]/g, '') }));

  const rekap = potongan
    .map((p) => {
      const r = kondisi.rusak ? ambil(p.key, 'r') : 0;
      const h = kondisi.hilang ? ambil(p.key, 'h') : 0;
      const b = kondisi.belum ? ambil(p.key, 'b') : 0;
      return { ...p, r, h, b, sisa: p.qty - r - h - b };
    })
    .filter((x) => x.r > 0 || x.h > 0 || x.b > 0 || x.sisa < 0);

  const lebih = rekap.filter((x) => x.sisa < 0);
  const nominalAngka = angka(nominal);
  const belumTotal = rekap.reduce((n, x) => n + x.b, 0);
  const rusakHilangTotal = rekap.reduce((n, x) => n + x.r + x.h, 0);
  const qtyTotal = potongan.reduce((n, p) => n + p.qty, 0);
  // Semua barang ditandai "belum kembali" = tidak ada yang berubah; tidak ada gunanya disimpan.
  const tidakBerubah = belumTotal > 0 && belumTotal === qtyTotal && rusakHilangTotal === 0;
  const sebagian = belumTotal > 0;
  const bolehKirim = !kirim && lebih.length === 0 && !tidakBerubah;
  const dendaAda = !!tx?.denda;   // denda hanya sekali per order (dicatat di tahap sebelumnya)

  const pilihKondisi = (nama) => {
    setKondisi((s) => {
      if (nama === 'lengkap') {
        if (!s.lengkap) return { ...s, lengkap: true, belum: false, rusak: false, hilang: false };
        return s;
      }
      const baru = { ...s, [nama]: !s[nama] };
      if (nama === 'rusak' || nama === 'hilang' || nama === 'belum') {
        baru.lengkap = !baru.rusak && !baru.hilang && !baru.belum;
      }
      return baru;
    });
  };

  const simpan = async (mode) => {
    if (!bolehKirim) return;
    const rusak = [];
    const hilang = [];
    const belum = [];
    potongan.forEach((p) => {
      const r = kondisi.rusak ? ambil(p.key, 'r') : 0;
      const h = kondisi.hilang ? ambil(p.key, 'h') : 0;
      const b = kondisi.belum ? ambil(p.key, 'b') : 0;
      if (r > 0) rusak.push({ product_id: p.product_id, size: p.size, pcs: r });
      if (h > 0) hilang.push({ product_id: p.product_id, size: p.size, pcs: h });
      if (b > 0) belum.push({ product_id: p.product_id, size: p.size, pcs: b });
    });
    const body = { mode, rusak, hilang, catatan_barang: catatan.trim() };
    if (belum.length) body.belum_kembali = belum;
    const aksesorisHilang = aksDibawa
      .filter((a) => (aksHilang[a.nama] || 0) > 0)
      .map((a) => ({ nama: a.nama, pcs: aksHilang[a.nama] }));
    if (aksesorisHilang.length) body.aksesoris_hilang = aksesorisHilang;
    if (isOwner && !dendaAda && kondisi.denda && nominalAngka > 0) {
      body.denda = { jenis, nominal: nominalAngka, sumber, catatan: dendaKet.trim() };
    }
    setKirim(true);
    try {
      const res = await api.post(`/transactions/${tx.id}/return`, body);
      const ekor =
        mode === 'laundry_open' ? 'laundry tanpa batas tanggal'
          : mode === 'laundry' ? 'masuk laundry' : 'langsung ready';
      let pesan = belum.length
        ? `Diterima sebagian → ${belum.reduce((n, x) => n + x.pcs, 0)} pcs belum kembali, stoknya tetap ditahan`
        : `Barang diterima → ${ekor}`;
      if (body.denda) {
        pesan += sumber === 'belum'
          ? ` · denda ${formatIDR(nominalAngka)} dicatat belum dibayar`
          : ` · denda ${formatIDR(nominalAngka)} masuk kas`;
      } else if (aksesorisHilang.length) {
        pesan += ' · aksesoris hilang tercatat, denda belum ditetapkan';
      }
      toast.success(pesan);
      // Barang hilang/rusak bisa membuat order mendatang kekurangan barang.
      // Pencatatan tetap disimpan; owner perlu tahu supaya bisa cari pengganti.
      const pk = res?.data?.peringatan_stok || [];
      if (pk.length) {
        toast.warning(`Barang ini sudah dipesan lagi: ${pk.join(' · ')}`, { duration: 15000 });
      }
      onClose?.();
      onDone?.(tx);
    } catch (err) {
      toast.error(apiError(err));
      setKirim(false);
    }
  };

  const baris = (p, jns, warna) => (
    <div key={`${p.key}-${jns}`} className="flex items-center gap-2 py-1.5 border-b border-brown/5 last:border-0">
      <span className="flex-1 min-w-0 text-xs">
        <b>{p.code}</b> {p.name}
        <span className="block text-[11px] text-mocha">ukuran {p.size} · {lengkapi ? 'masih di customer' : 'disewa'} {p.qty} pcs</span>
      </span>
      <input
        data-testid={`${testid}-${jns}-${p.product_id}-${p.size}`}
        type="text"
        inputMode="numeric"
        value={jml[`${p.key}|${jns}`] ?? ''}
        placeholder="0"
        onChange={(e) => setAmbil(p.key, jns, e.target.value)}
        className={`w-16 rounded-lg border bg-ivory/50 px-2 py-1.5 text-sm text-center outline-none focus:ring-2 focus:ring-brown/30 ${warna}`}
      />
      <span className="text-[11px] text-mocha">pcs</span>
    </div>
  );

  const pilihan = (nama, judul, sub, dites) => (
    <label className="flex items-start gap-2.5 py-2 cursor-pointer" data-testid={`${testid}-opt-${dites}`}>
      <input
        type="checkbox"
        checked={!!kondisi[nama]}
        onChange={() => pilihKondisi(nama)}
        className="mt-0.5 h-4 w-4 accent-[#3B281C]"
      />
      <span className="text-sm">
        <b>{judul}</b>
        <span className="block text-[11px] text-mocha">{sub}</span>
      </span>
    </label>
  );

  if (!tx) return null;

  return (
    <Dialog open={!!open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent data-testid={`${testid}-dialog`} className="max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-brown-dark">{lengkapi ? 'Lengkapi Pengembalian' : 'Terima Pengembalian'}</DialogTitle>
        </DialogHeader>

        <p className="text-xs text-mocha -mt-1">
          Order <b className="text-brown-dark">{tx.transaction_number}</b> ({tx.customer_name})
          {tx.return_date ? ` · kembali ${tx.return_date}` : ''}
          {lengkapi && tx.sebagian_date ? ` · sisa dari penerimaan ${tx.sebagian_date}` : ''}
          {telat > 0 && (
            <span className="ml-1.5 inline-block rounded-full bg-orange-100 text-orange-700 px-2 py-0.5 text-[10px] font-bold">
              telat {telat} hari
            </span>
          )}
        </p>

        <div className="mt-1">
          {pilihan('lengkap', 'Barang lengkap & utuh', 'Semua kembali, tidak ada yang rusak atau hilang', 'lengkap')}

          {pilihan('belum', 'Ada yang belum kembali', 'Masih di customer. Order tetap aktif dan stok barang itu tetap ditahan. Bisa dilengkapi lain hari.', 'belum')}
          {kondisi.belum && (
            <div className="ml-6 mb-2 rounded-xl bg-ivory/70 border border-brown/10 p-3">
              {potongan.map((p) => baris(p, 'b', 'border-amber-400'))}
            </div>
          )}

          {pilihan('rusak', 'Ada yang rusak', 'Tetap terhitung milikmu, tapi berhenti bisa dibooking', 'rusak')}
          {kondisi.rusak && (
            <div className="ml-6 mb-2 rounded-xl bg-ivory/70 border border-brown/10 p-3">
              {potongan.map((p) => baris(p, 'r', 'border-orange-300'))}
            </div>
          )}

          {pilihan('hilang', 'Ada yang hilang', 'Dikurangi dari stok secara permanen', 'hilang')}
          {kondisi.hilang && (
            <div className="ml-6 mb-2 rounded-xl bg-ivory/70 border border-brown/10 p-3">
              {potongan.map((p) => baris(p, 'h', 'border-red-300'))}
            </div>
          )}

          {rekap.length > 0 && (
            <div className="my-2 rounded-xl border border-brown/10 overflow-hidden">
              <table className="w-full text-[11px]" data-testid={`${testid}-rekap`}>
                <thead className="bg-ivory/70 text-mocha">
                  <tr>
                    <th className="text-left px-2 py-1.5 font-semibold">Barang</th>
                    <th className="px-2 py-1.5 font-semibold">{lengkapi ? 'Sisa' : 'Sewa'}</th>
                    <th className="px-2 py-1.5 font-semibold">Belum</th>
                    <th className="px-2 py-1.5 font-semibold">Rusak</th>
                    <th className="px-2 py-1.5 font-semibold">Hilang</th>
                    <th className="px-2 py-1.5 font-semibold">Kembali baik</th>
                  </tr>
                </thead>
                <tbody>
                  {rekap.map((x) => (
                    <tr key={x.key} className={x.sisa < 0 ? 'text-danger font-bold' : ''}>
                      <td className="px-2 py-1.5">{x.code} <span className="text-mocha">{x.size}</span></td>
                      <td className="px-2 py-1.5 text-center">{x.qty}</td>
                      <td className="px-2 py-1.5 text-center">{x.b}</td>
                      <td className="px-2 py-1.5 text-center">{x.r}</td>
                      <td className="px-2 py-1.5 text-center">{x.h}</td>
                      <td className="px-2 py-1.5 text-center">{x.sisa}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {lebih.length > 0 && (
            <p className="rounded-xl bg-danger/10 text-danger text-[11px] font-bold px-3 py-2 mb-2"
               data-testid={`${testid}-lebih`}>
              Jumlahnya melebihi yang disewa. Betulkan dulu sebelum disimpan.
            </p>
          )}

          {sebagian && !tidakBerubah && (
            <p className="rounded-xl bg-warning/15 text-[#8a5f1e] text-[11px] font-semibold px-3 py-2 mb-2" data-testid={`${testid}-sebagian-info`}>
              Belum lengkap: <b>{belumTotal} pcs belum kembali</b>. Order tetap aktif dan stok {belumTotal} pcs itu tetap ditahan.
              Yang sudah kembali langsung tersedia lagi. Lengkapi lain hari lewat tombol "Lengkapi Pengembalian".
            </p>
          )}
          {tidakBerubah && (
            <p className="rounded-xl bg-danger/10 text-danger text-[11px] font-bold px-3 py-2 mb-2" data-testid={`${testid}-tidak-berubah`}>
              Semua barang masih belum kembali, jadi tidak ada yang perlu dicatat. Tutup dialog ini, atau isi jumlah yang sudah kembali.
            </p>
          )}

          {(kondisi.rusak || kondisi.hilang) && (
            <input
              data-testid={`${testid}-catatan`}
              type="text"
              value={catatan}
              onChange={(e) => setCatatan(e.target.value)}
              placeholder='Keterangan barang, mis. "jilbab bawaan set hilang"'
              className="w-full rounded-xl border border-input bg-ivory/50 px-3 py-2 text-xs outline-none focus:ring-2 focus:ring-brown/30 mb-1"
            />
          )}

          {aksDibawa.length > 0 && (
            <div className="my-2 rounded-xl border border-brown/10 bg-ivory/70 p-3" data-testid={`${testid}-aksesoris`}>
              <p className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Cek aksesoris bawaan</p>
              {aksDibawa.map((a) => (
                <div key={a.nama} className="flex items-center gap-2 py-1.5 border-b border-brown/5 last:border-0">
                  <span className="flex-1 min-w-0 text-xs">
                    <b>{a.nama}</b>
                    <span className="block text-[11px] text-mocha">dibawa {a.dibawa} pcs · stok tidak berubah</span>
                  </span>
                  <span className="text-[11px] text-mocha">hilang</span>
                  <Stepper
                    testid={`${testid}-aks-${a.nama.replace(/\s+/g, '-')}`}
                    value={aksHilang[a.nama] || 0}
                    max={a.dibawa}
                    onChange={(v) => ubahAks(a.nama, a.dibawa, v)}
                  />
                </div>
              ))}
              {aksJumlahHilang > 0 && (
                <p className="text-[11px] text-orange-700 font-semibold mt-2" data-testid={`${testid}-aks-info`}>
                  {isOwner
                    ? 'Isi nominal ganti rugi di bagian denda di bawah. Dikosongkan = aksesoris tetap tercatat hilang, dendanya bisa diisi nanti lewat kartu Denda di detail order.'
                    : 'Aksesoris hilang tercatat. Nominal ganti rugi nanti diisi owner.'}
                </p>
              )}
            </div>
          )}

          {isOwner && dendaAda && (
            <p className="text-[11px] text-mocha py-1" data-testid={`${testid}-denda-sudah`}>
              Denda untuk order ini sudah dicatat di penerimaan sebelumnya. Perubahannya lewat kartu Denda di detail order.
            </p>
          )}
          {isOwner && !dendaAda && pilihan('denda', 'Ada denda', 'Keterlambatan, kerusakan, atau kehilangan', 'denda')}

          {isOwner && !dendaAda && kondisi.denda && (
            <div className="ml-6 rounded-xl bg-ivory/70 border border-brown/10 p-3 space-y-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Jenis denda</label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {JENIS.map((j) => (
                    <button
                      key={j}
                      type="button"
                      data-testid={`${testid}-jenis-${j}`}
                      onClick={() => setJenis((s) => (s.includes(j) ? s.filter((x) => x !== j) : [...s, j]))}
                      className={`rounded-full px-3 py-1 text-[11px] font-semibold border ${
                        jenis.includes(j)
                          ? 'bg-brown text-white border-brown'
                          : 'bg-white text-mocha border-brown/15'}`}
                    >
                      {j}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-mocha mt-1.5">
                  {telat > 0
                    ? `Aplikasi membaca telat ${telat} hari. Nominalnya kamu yang tentukan.`
                    : 'Dikembalikan tepat waktu.'}
                </p>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Nominal denda</label>
                <MoneyInput
                  data-testid={`${testid}-nominal`}
                  value={nominal}
                  onChange={setNominal}
                  placeholder="0"
                  className="mt-1 w-full rounded-xl border border-input bg-white px-4 py-2.5 text-base font-bold outline-none focus:ring-2 focus:ring-brown/30"
                />
                {nominalAngka > 0 && (
                  <p className="text-[11px] text-mocha mt-1">terbaca: <b>{formatIDR(nominalAngka)}</b></p>
                )}
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Keterangan denda</label>
                <input
                  data-testid={`${testid}-denda-ket`}
                  type="text"
                  value={dendaKet}
                  onChange={(e) => { setDendaKet(e.target.value); setKetManual(true); }}
                  placeholder="boleh dikosongkan"
                  className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2 text-xs outline-none focus:ring-2 focus:ring-brown/30"
                />
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Dibayar lewat apa?</label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {SUMBER.map((s) => (
                    <button
                      key={s.v}
                      type="button"
                      data-testid={`${testid}-sumber-${s.v}`}
                      onClick={() => setSumber(s.v)}
                      className={`rounded-full px-3 py-1 text-[11px] font-semibold border ${
                        sumber === s.v
                          ? 'bg-brown text-white border-brown'
                          : 'bg-white text-mocha border-brown/15'}`}
                    >
                      {s.t}
                    </button>
                  ))}
                </div>
                {nominalAngka > 0 && sumber === 'belum' && (
                  <p className="text-[11px] text-orange-700 mt-1.5 font-semibold">
                    Belum menyentuh saldo kas. Masuk daftar denda belum dibayar di menu Kas.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          <button
            data-testid={`${testid}-cancel`}
            onClick={() => onClose?.()}
            className="rounded-xl border border-brown/15 text-mocha px-4 py-2.5 text-sm font-semibold"
          >
            Batal
          </button>
          {sebagian ? (
            <button
              data-testid={`${testid}-sebagian`}
              disabled={!bolehKirim}
              onClick={() => simpan('ready')}
              className="flex-1 rounded-xl bg-amber-600 text-white px-4 py-2.5 text-sm font-bold hover:bg-amber-700 disabled:opacity-40"
            >
              Simpan: ada yang belum kembali
            </button>
          ) : (
            <>
            <button
              data-testid={`${testid}-ready`}
              disabled={!bolehKirim}
              onClick={() => simpan('ready')}
              className="flex-1 rounded-xl bg-success text-white px-4 py-2.5 text-sm font-bold hover:bg-success/90 disabled:opacity-40"
            >
              Langsung Ready
            </button>
            <button
              data-testid={`${testid}-laundry`}
              disabled={!bolehKirim}
              onClick={() => simpan('laundry')}
              className="flex-1 rounded-xl bg-brown text-white px-4 py-2.5 text-sm font-bold hover:bg-brown-med disabled:opacity-40"
            >
              Masuk Laundry (+2 hari)
            </button>
            <button
              data-testid={`${testid}-laundry-open`}
              disabled={!bolehKirim}
              onClick={() => simpan('laundry_open')}
              className="flex-1 rounded-xl bg-orange-700 text-white px-4 py-2.5 text-sm font-bold hover:bg-orange-800 disabled:opacity-40"
            >
              Laundry Tanpa Batas
            </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
